import { useState, useEffect } from "react";
import { useParams } from "react-router";
// contexts
import { useUserData } from "../context/UserDataContext";
// hooks
import useSend from "../hooks/useSend";
import useStorage, { StorageKeys } from "../hooks/useStorage";
// services
import { Storage } from "../services/storage";
// types
import type { UserData, UserId } from "../types/user";
import type { CmdGetB2CUser, CmdGetUser, MsgResponseGetUser, MsgResponseGetB2CUser } from "../types/gupport";

type RouteParams = {
	userId?: UserId;
};

const useSelectedUser = () => {
	const send = useSend();
	const params = useParams<RouteParams>();

	const { ready } = useUserData();

	const [loading, setLoading] = useState(false);
	const [isError, setIsError] = useState(false);
	const [selectedUser, setSelectedUser] = useState<UserData | null>(null);
	const [storedUserId] = useStorage(StorageKeys.userId);
	const [isAdvancedSearchEnabled] = useStorage(StorageKeys.isAdvancedSearchEnabled);

	useEffect(() => {
		if (params.userId || storedUserId) {
			if (ready) {
				const handleFetchUserResult = (error: Error | null, msg: MsgResponseGetUser | MsgResponseGetB2CUser) => {
					if (error || msg.payload.status === "error") {
						setIsError(true);
						setSelectedUser(null);
					} else {
						Storage.set(StorageKeys.userId, params.userId ?? storedUserId);
						setSelectedUser(msg.payload.data);
						setIsError(false);
					}
					setLoading(false);
				};
				setLoading(true);
				if (isAdvancedSearchEnabled) {
					const cmd = {
						action: "getB2CUser",
						sub: params.userId ?? storedUserId,
					} as const satisfies CmdGetB2CUser;
					send(cmd, handleFetchUserResult);
				} else {
					const cmd = {
						action: "getUser",
						username: params.userId ?? storedUserId,
					} as const satisfies CmdGetUser;
					send(cmd, handleFetchUserResult);
				}
			}
		} else {
			Storage.remove(StorageKeys.userId);
			Storage.remove(StorageKeys.usersTab);
			setSelectedUser(null);
		}
	}, [params.userId, ready, isAdvancedSearchEnabled]);

	return { loading, selectedUser, isError };
};

export default useSelectedUser;
